/**
 * The PDF export's status (decisions 8, 14; part 21, table K): under the toolbar while an export runs or has something to
 * say, never over the text, so the writer goes on typing while a book exports. Its progress and Cancel; then the finished
 * file to save, or what went wrong with Try again.
 */
import type { ExportState } from '../export/controller.ts';
import { focusOn } from './announce.ts';
import { el, when } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import { JobProgress, JobStatus } from './job-status.ts';
import type { JobCount } from './job-status.ts';
import type { Shell } from './shell.ts';
import Button from '../ui/Button.ts';
import { component } from '../ui/solid.ts';

/** What the status says (K-rows' outcomes): reading, how far the layout has come, or putting the file together. */
export function exportText(shell: Shell, state: ExportState): string {
  const { t } = shell;
  switch (state.kind) {
    case 'idle':
      return '';
    case 'reading':
      return t('export.reading');
    case 'exporting':
      return state.done >= state.total ? t('export.finishing') : t('export.progress', { percent: Math.floor((100 * state.done) / state.total) });
    case 'ready':
      return t('export.ready');
    case 'failed':
      return state.reason === 'unreadable' ? t('export.unreadable') : t('export.failed');
  }
}

/**
 * Part 11c, A13: what the export announces: its phases (reading, making, finishing, ready, failed), never each percent
 * the status shows. Making starts before the first page is counted (0 of 0).
 */
export function exportNews(shell: Shell, state: ExportState): string {
  return state.kind === 'exporting' && (state.done < state.total || state.total === 0) ? shell.t('export.making') : exportText(shell, state);
}

/**
 * The status region, shown while the export isn't idle; what it announces is {@link exportNews}, said by the workspace's
 * announcers (A13). Each button that
 * goes with the state it acts on leaves the focus where the next step is (A24): Cancel and Dismiss on the toolbar's
 * Export PDF, Try again on the new Cancel.
 */
export function ExportStatus(shell: Shell): Child {
  const { t, exporter } = shell;
  const state = (): ExportState => exporter.state();
  const running = (): boolean => state().kind === 'reading' || state().kind === 'exporting';
  const count = (): JobCount => {
    const now = state();
    return now.kind === 'exporting' ? now : null;
  };
  return [
    when(
      () => state().kind !== 'idle',
      () =>
        JobStatus({
          class: 'export-pdf-status',
          label: t('export.label'),
          tone: () => (state().kind === 'failed' ? 'danger' : state().kind === 'ready' ? 'success' : 'neutral'),
          children: [
            el('p', {}, () => exportText(shell, state())),
            JobProgress(t('export.label'), count),
          ],
          actions: [
            when(running, () =>
              component(Button, {
                size: 'sm',
                class: 'cancel-export',
                onClick: () => {
                  exporter.cancel();
                  focusOn(shell, '.export-pdf');
                },
                children: t('export.cancel'),
              }),
            ),
            () => {
              const now = state();
              return now.kind === 'ready' ? component(Button, { size: 'sm', class: 'save-pdf', href: now.url, download: now.name, children: t('export.save') }) : null;
            },
            when(
              () => state().kind === 'failed',
              () =>
                component(Button, {
                  size: 'sm',
                  onClick: () => {
                    exporter.start();
                    focusOn(shell, '.cancel-export');
                  },
                  children: t('export.retry'),
                }),
            ),
            when(
              () => !running(),
              () =>
                component(Button, {
                  variant: 'ghost',
                  size: 'sm',
                  onClick: () => {
                    exporter.dismiss();
                    focusOn(shell, '.export-pdf');
                  },
                  children: t('export.dismiss'),
                }),
            ),
          ],
        }),
    ),
  ];
}
